/**
 * Batch Studio renders image adjustments as a CSS `filter` string rather than the
 * editor's PIXI adjustment pipeline (AdjustmentFilterFactory). Only the subset of
 * adjustments that map cleanly to CSS filter functions is exposed in batch:
 *   exposure → brightness, contrast → contrast, saturation → saturate, blur → blur.
 * Temperature/tint are approximated with hue-rotate (no exact CSS equivalent).
 *
 * The same conversion drives the live card preview and the baked export, so the
 * look stays identical regardless of pixel size. Mirrors shadowCss.ts.
 */

export type AdjustKey = 'exposure' | 'contrast' | 'saturation' | 'blur' | 'temperature' | 'tint'

export interface AdjustmentsConfig {
  exposure: number
  contrast: number
  saturation: number
  blur: number
  temperature: number
  tint: number
}

export interface EditorAutoAdjustments {
  exposure: number
  contrast: number
  vibrance: number
  temperature: number
  tint: number
}

/**
 * Slider logic min/max/default for the batch subset. Symmetric ranges centered on
 * a `default` that maps to the CSS identity (no change); blur is one-sided.
 */
export const ADJUST_RANGE: Record<AdjustKey, { min: number; max: number; default: number }> = {
  exposure:    { min: -1, max: 1, default: 0 },
  contrast:    { min: -1, max: 1, default: 0 },
  saturation:  { min: -1, max: 1, default: 0 },
  temperature: { min: -1, max: 1, default: 0 },
  tint:        { min: -1, max: 1, default: 0 },
  blur:        { min: 0,  max: 1, default: 0 },
}

/** The batch subset of adjustment sliders, in display order. */
export const BATCH_ADJUST_KEYS: AdjustKey[] = [
  'exposure',
  'contrast',
  'saturation',
  'blur',
  'temperature',
  'tint',
]

/** Full blur (value 1) blurs by this fraction of the drawn image's longest edge. */
const BLUR_MAX_FRACTION = 0.04

/** Approximate degrees of hue rotation at full temperature/tint. */
const HUE_DEGREES = 30

/** An AdjustmentsConfig with every key at its CSS-identity default. */
export function defaultAdjustments(): AdjustmentsConfig {
  return Object.fromEntries(
    (Object.keys(ADJUST_RANGE) as AdjustKey[]).map((key) => [key, ADJUST_RANGE[key].default]),
  ) as unknown as AdjustmentsConfig
}

/** True when every adjustment sits at its default (CSS identity). */
export function isDefaultAdjustments(adj: AdjustmentsConfig | null | undefined): boolean {
  if (!adj) return true
  return (Object.keys(ADJUST_RANGE) as AdjustKey[]).every(
    (key) => (adj[key] ?? ADJUST_RANGE[key].default) === ADJUST_RANGE[key].default,
  )
}

/**
 * Maps editor auto-adjust values into batch slider units so BOTH surfaces render
 * the same look. The mappings mirror the editor's actual filters:
 *  - exposure is multiplicative ×2^e (ExposureFilter) and batch applies
 *    `brightness(1 + value)`, so value = 2^e − 1.
 *  - contrast bzrt maps through ColorControlFilter.normalize to the shader factor,
 *    which is exactly the CSS `contrast(factor)` batch applies, so
 *    value = normalize(bzrt) − 1.
 * Editor `vibrance` has no batch slider, so it drives `saturation` (saturate).
 * Batch sliders are normalized to [-1, 1], identity at 0.
 */
export function normalizeEditorAdjustmentsToBatch(raw: EditorAutoAdjustments): Partial<AdjustmentsConfig> {
  const clamp1 = (v: number) => Math.max(-1, Math.min(1, v))
  // ColorControlFilter.normalize: bzrt [0.75,1.25] → shader contrast [0.05,2].
  const shaderContrast = ((raw.contrast - 0.75) / 0.5) * 1.95 + 0.05
  return {
    exposure: clamp1(Math.pow(2, raw.exposure) - 1),
    contrast: clamp1(shaderContrast - 1),
    saturation: clamp1(raw.vibrance / 1.2),
    temperature: clamp1((raw.temperature - 6500) / 4500),
    tint: clamp1(raw.tint / 100),
  }
}

/**
 * Builds a CSS `filter` value from an AdjustmentsConfig and the drawn image's
 * longest edge in px (blur scales against it). Returns '' when all-default.
 */
export function adjustmentsToCssFilter(
  adj: AdjustmentsConfig | null | undefined,
  maxEdgePx: number,
): string {
  if (!adj) return ''
  const parts: string[] = []

  const exposure = adj.exposure ?? 0
  if (exposure !== 0) parts.push(`brightness(${Math.max(0, 1 + exposure).toFixed(3)})`)

  const contrast = adj.contrast ?? 0
  if (contrast !== 0) parts.push(`contrast(${Math.max(0, 1 + contrast).toFixed(3)})`)

  const saturation = adj.saturation ?? 0
  if (saturation !== 0) parts.push(`saturate(${Math.max(0, 1 + saturation).toFixed(3)})`)

  // Temperature: warm (+) rotates toward red/orange, cool (−) toward blue. Approx.
  const temperature = adj.temperature ?? 0
  if (temperature !== 0) parts.push(`hue-rotate(${(-temperature * HUE_DEGREES).toFixed(1)}deg)`)

  // Tint: green/magenta axis, opposite hue direction. Approx.
  const tint = adj.tint ?? 0
  if (tint !== 0) parts.push(`hue-rotate(${(tint * HUE_DEGREES).toFixed(1)}deg)`)

  const blur = adj.blur ?? 0
  if (blur > 0 && maxEdgePx > 0) {
    parts.push(`blur(${(blur * maxEdgePx * BLUR_MAX_FRACTION).toFixed(2)}px)`)
  }

  return parts.join(' ')
}
